import {
  ChangeDetectorRef, ChangeDetectionStrategy, Component, ElementRef, ViewChild,
  NgZone, OnInit, OnDestroy
} from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd, NavigationStart } from '@angular/router';
import { Location } from '@angular/common';
import { Subscription } from 'rxjs';
import * as _ from 'lodash';
import {filter, first, takeWhile} from 'rxjs/operators';


// utils, data
import { actions } from './actions';
import { PACT } from 'shared/fitments/contextual-actions/config';

// models
import { SearchModel } from 'shared/search/search.model';
import { ReturnRouteModel } from 'shared/return-route/return-route.model';
import { QueueProcess } from 'shared/process/queue-process.model';

// components
import { FitmentsSearchPageComponent } from 'shared/fitments/fitments-search-page.component';
import { AssignToProductsModalComponent } from './components/assign-to-products-modal/assign-to-products-modal.component';
import { LoadProductModalComponent } from './components/load-product-modal/load-product-modal.component';
import { MyFitmentsHeaderComponent } from './components/my-fitments-header/my-fitments-header.component';

// services
import { SearchPageService } from 'shared/search-page/search-page.service';
import { FitmentsListService } from 'shared/fitments/fitments-list.service';
import { BrandService } from 'shared/brand/brand.service';
import { ProcessService } from 'shared/process/process.service';
import { ScrollToService } from 'shared/base/scroll-to.service';
import { MyFitmentsSearchService } from 'shared/fitments/search/my-fitments-search.service';
import { AppRoutingService } from '../app-routing.service';
import { MyProductsStoreDataService } from 'shared/my-products-store-data/my-products-store-data.service';
import { AuthService } from 'shared/auth/auth.service';
import { FitmentsService } from 'shared/fitments/fitments.service';
import { ItemDetailService } from 'shared/item-detail/item-detail.service';
import { PermissionsService } from 'shared/permissions/permissions.service';
import { SavedSearchesService } from 'shared/saved-searches/saved-searches.service';
import { UserService } from 'shared/user/user.service';
import { ReturnRouteService } from 'shared/return-route/return-route.service';
import {ProductPanelComponent} from '../product-panel/product-panel.component';



@Component({
  moduleId: module.id,
  selector: 'app-my-fitments',
  templateUrl: 'my-fitments.component.html',
  styleUrls: ['my-fitments.component.scss'],
  providers: [
    {provide: 'LIST_TYPE', useValue: 'fitment'},
    FitmentsListService,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyFitmentsComponent extends FitmentsSearchPageComponent implements OnInit, OnDestroy {

  @ViewChild(AssignToProductsModalComponent, {static: false})
  assignToProduct: AssignToProductsModalComponent | void;

  @ViewChild(LoadProductModalComponent, {static: false})
  loadProductModal: LoadProductModalComponent | void;

  @ViewChild(MyFitmentsHeaderComponent, {static: false})
  declare headerEl: MyFitmentsHeaderComponent | void;

  actions: any = actions;
  ghostValue = 'my-fitments';
  searchResultsColumns: any = [];
  researchPlaceholder = 'SEARCH | E.G. YEAR, MAKE, MODEL';
  isResearchMode = true;
  showSidedrawer: boolean = true; // use this field to show sidedrawer
  lastColumns: Array<string> = [
    'qty', 'position', 'mfrLabel', 'notes', 'partTerminologies',
    'qualifiers', 'digitalAssets',
  ];
  protected selectedProducts;
  scrollLeftBorder: number = 0.5; // border after which we need to fix first column
  private previousPageUrl: string;
  private routerChangeSub: Subscription;

  constructor(
    public searchService: MyFitmentsSearchService,
    protected brandService: BrandService,
    protected router: Router,
    protected pageService: SearchPageService,
    protected processService: ProcessService,
    protected scrollToService: ScrollToService,
    protected listsService: FitmentsListService,
    protected appRoutingService: AppRoutingService,
    protected permissionsService: PermissionsService,
    protected ngZone: NgZone,
    protected ref: ChangeDetectorRef,
    protected route: ActivatedRoute,
    private myProductsStoreDataService: MyProductsStoreDataService,
    private authService: AuthService,
    private itemDetailService: ItemDetailService,
    protected fitmentsService: FitmentsService,
    protected location: Location,
    protected savedSearchesService: SavedSearchesService,
    protected userService: UserService,
    protected returnRouteService: ReturnRouteService,
  ) {
    super(
      searchService,
      brandService,
      pageService,
      processService,
      scrollToService,
      appRoutingService,
      listsService,
      permissionsService,
      ngZone,
      ref,
      route,
      location,
      savedSearchesService,
      router,
      userService,
      fitmentsService,
    );
  }

  /*******************************************
  * COMPONENT GETTERS
  *******************************************/

  get brandId(): any {
    return this.brandService.getBrand();
  }

  get searchInputValue(): string {
    // handle it in the components
    // to get search value from header search input
    return this.headerEl && this.headerEl.searchInputValue;
  }

  get showDefaultStateMessage(): boolean {
    return !(this.searchResults.length || this.searchModelFilters.length);
  }

  get rowStatusHidden(): boolean {
    return !this.searchModelListFilters.length;
  }

  // implemented
  get headerColor(): string {
    return 'midgrey';
  }

  get page(): string {
    const url = this.route.snapshot.url;
    return url[0].path;
  }

  get isLeftTabActive(): boolean {
    return this.page === 'fitments';
  }

  get isRightTabActive(): boolean {
    return this.page === 'fitments-library';
  }

  // redefine parent method
  get allowSearchRun(): boolean {
    return this.page === 'fitments';
  }

  /*******************************************
  * COMPONENT HOOKS
  *******************************************/

  ngOnInit() {
    super.ngOnInit();
    this.initFitments();
    this.previousPageUrl = this.appRoutingService.urlAfterRedirects;
    this.searchService.updateColumnsEvent
    .pipe(takeWhile(() => this.isPageActive))
    .subscribe((columns: any) => {
      // order columns if they were changed in the column builder
      const orderedColumns = _.sortBy(columns, (columnName: string) => {
        const index = this.searchModel && _.indexOf(this.searchModel.columns, columnName) || -1;
        // put new columns after fitment assign to the end
        if (index === -1) { return columns.length; }
        return index;
      });

       _.pullAll(orderedColumns, this.lastColumns);
      const columnsData: any = this.getColumnsData(orderedColumns);
      this.searchResultsColumns = this.createNewColumns(columnsData);
      if (this.searchModel instanceof SearchModel) {
        this.searchModel.columns = _.chain(this.searchResultsColumns).tail().map('columnName').value() as Array<string>;
      }
    });
  }


  ngOnDestroy() {
    super.ngOnDestroy();
  }

  /*******************************************
  * COMPONENT HANDLERS
  *******************************************/

  onPageActionSelect($event: any) {
    switch ($event.action) {
      case 'edit-columns':
        super.onPageActionSelect($event);
        break;
      case 'load-product':
        if (this.loadProductModal) {
          this.loadProductModal.show();
        }
        break;
    }
  }

  onActionSelected($event: any) {
    switch ($event.action) {
      case PACT.ASSIGN_TO_PRODUCT.action:
        if (this.assignToProduct) {
          this.assignToProduct.show();
        }
        break;
      default:
        super.onActionSelected($event);
    }
  }

  // research state
  onAssignToProducts(selectedProducts: Array<any>) {
    this.selectedProducts = selectedProducts;
    if (this.assignToProduct) {
      this.assignToProduct.hide();
    }
    this.startLoading();
    const productIds = _.compact(_.map(selectedProducts, (item: any) => {
      return _.get(item, '_id.$oid');
    }));

    this.assignToProductsRequest(productIds)
    .pipe(first(), takeWhile(() => this.isPageActive))
    .subscribe((res: any) => {
      this.deselectAll();
      this.stopLoading();
      if (res.processId) {
        const process: QueueProcess = new QueueProcess(res.processId, 0);
        this.startProcessingModal(process);
      } else {
        _.defer(() => {
          this.returnRouteService.push(new ReturnRouteModel(`/${this.route.snapshot.url.join('/')}`, {smlocal: 1}));
          this.router.navigate([`product-details-v2/${selectedProducts[0].itemId}/fitments`]);
        });
      }
    });
  }

  onAssignToProductLists(listIds: Array<any>) {
    this.selectedProducts = null;
    if (this.assignToProduct) {
      this.assignToProduct.hide();
    }
    let request;
    this.startLoading();
    if (this.selectAll === 'in-list') {
      request = this.fitmentsService.assignAllAppsInSearchToProductList(
        this.brandId,
        listIds,
        this.searchModel && this.searchService.getQueryString(this.searchModel) || '',
        this.searchService.listFilterId
      );
    } else {
      request = this.fitmentsService.assignToProductLists(
        this.brandId,
        listIds,
        this.selectedAssignmentValues,
        this.searchModel && this.searchModel.columns || []
      );
    }
    request
    .pipe(first(), takeWhile(() => this.isPageActive))
    .subscribe((res: any) => {
      this.deselectAll();
      this.stopLoading();
      if (res.processId) {
        const process: QueueProcess = new QueueProcess(res.processId, 0);
        this.startProcessingModal(process);
        return;
      } else {
        if (_.isArray(res) && res.length > 0) {
            res = res[0];
            if (res.processId) {
                const process: QueueProcess = new QueueProcess(res.processId, 0);
                this.startProcessingModal(process);
                return;
            }
        }
        if (this.searchModel instanceof SearchModel) {
          this._searchModel.next(this.searchModel);
        }
      }
    });
  }

  onProductLoad(selectedProduct: any) {
    this.router.navigate([`product-details-v2/${selectedProduct.itemId}/fitments`]);
  }

  onLeftTabClick() {
    this.router.navigate(['/fitments']);
  }

  onRightTabClick() {
    this.router.navigate(['/fitments-library']);
  }

  /*******************************************
  * COMPONENT PUBLIC METHODS
  *******************************************/

  public changeColumns(selectedColumns: Array<any>): void {
    // create new columns
    super.changeColumns(selectedColumns);
    // run search
    this.deselectAll();
    if (this.searchModel && this.searchModel.filters.length) {
      this._searchModel.next(this.searchModel);
    }
  }

  public initSubscriptions() {
    super.initSubscriptions();
    this.routerChangeSub = this.subscribeRouterChange();
  }


  /*******************************************
  * COMPONENT PRIVATE METHODS
  *******************************************/

  private initFitments(): void {
    if (this.storedSearchModel)  {
      this.searchModel = this.storedSearchModel;
    } else if (!this.searchModel) {
      this.searchModel = new SearchModel();
    }
    if (this.storedFitmentsColumns) {
      this.searchResultsColumns = this.storedFitmentsColumns;
    } else {
      this.searchResultsColumns = this.getDefaultColumns();
    }
    this.ref.markForCheck();
    _.defer(() => {
      if (this.searchModel instanceof SearchModel) {
        this._searchModel.next(this.searchModel);
      }
    });
  }

  private subscribeRouterChange(): Subscription {
    return this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.handleRouterChange(event);
      });
  }

  private handleRouterChange(event?: NavigationEnd): void {
    this.unsubscribeSearchResultsScroll();
    this.showEmptyResultsState = false;
    if (this.allowSearchRun) {
      this.searchService.searchResults = [];
      this.searchService.selectAllChecked = [];
      this.searchService.selectedItems = [];
      this.searchService.allSelected = false;
      if (this.searchModel instanceof SearchModel) {
        this._searchModel.next(this.searchModel);
      }
      this.subscrubeSearchResultsScroll();
    }
  }

  handleCompleteQueueProcess(queueProcess: QueueProcess): void {
    super.handleCompleteQueueProcess(queueProcess);
    // or show processing complete toast
    // this.toasterService.pop(processingCompleteToast());
    if (this.selectedProducts && this.selectedProducts.length) {
        _.defer(() => {
          this.returnRouteService.push(new ReturnRouteModel(`/${this.route.snapshot.url.join('/')}`, {smlocal: 1}));
          this.router.navigate([`product-details-v2/${this.selectedProducts[0].itemId}/fitments`]);
          this.selectedProducts = null;
        });
    }
  }
}
